import React, { FC, useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import classNames from 'classnames'
import ModalTemplate from 'uiKit/ModalTemplate'
import { CurrentSupervisionTab } from './CurrentSupervisionTab'
import { CompletedSupervisionTab } from './CompletedSupervisionTab'
import { CurrentSessionType, UserType } from '../../model/usersModel'
import classes from '../../../widget/components/AddGreetingModal/styles.module.scss'
import { SupervisionStatuses } from '../../helpers/constants'
import { isQcsBot } from 'constants/qcs'
import { getBotPackage } from 'tabs/home/api/botPackage'
import { isBotPackageSupervisionEnabled } from 'tabs/home/api/usageLimits'

type TabType = 'Current' | 'Completed'
const ALL_TABS: TabType[] = ['Current', 'Completed']

interface Props {
  botId: string
  isOpenSupervisionManagementModal: boolean
  onClose: () => void
  selectedUser: UserType
  updateUserSupervisionStatus: (userId: number, newStatus: string) => void
}

export const SupervisionManagementModal: FC<Props> = ({
  botId,
  isOpenSupervisionManagementModal,
  onClose,
  selectedUser,
  updateUserSupervisionStatus,
}) => {
  const activeBot = useSelector((state: any) => state.activeBot)
  const isQcs = isQcsBot(activeBot || botId)
  const [supervisionEnabled, setSupervisionEnabled] = useState(true)
  const tabs = useMemo<TabType[]>(
    () => (supervisionEnabled ? ALL_TABS : ALL_TABS.filter(tab => tab !== 'Current')),
    [supervisionEnabled],
  )
  const [selectedTab, setSelectedTab] = useState<TabType>(tabs[0])
  const [hasExtraUpdate, setHasExtraUpdate] = useState(false)
  const [currentSession, setCurrentSession] = useState<CurrentSessionType>({
    completedAt: '',
    id: '',
    professional: undefined,
    scheduledAt: '',
    summary: {
      aiSummary: undefined,
      managerSummary: '',
      finalReportGenerated: false,
      hasManagerSummary: false,
    },
    updatedAt: '',
    status: SupervisionStatuses.NOT_ASSIGNED,
    createdAt: '',
    deadlineAt: '',
    outputFormFileName: '',
    transcriptFileName: '',
  })

  useEffect(() => {
    if (!botId || !isOpenSupervisionManagementModal) return undefined
    // CareBrain has no package assignment; a 404 here must not be requested.
    if (!isQcs) {
      setSupervisionEnabled(true)
      return undefined
    }
    const controller = new AbortController()
    getBotPackage(botId, controller.signal)
      .then(assignment => {
        if (controller.signal.aborted) return
        const enabled = isBotPackageSupervisionEnabled(assignment)
        setSupervisionEnabled(enabled)
        if (!enabled) setSelectedTab('Completed')
      })
      .catch(() => {
        if (!controller.signal.aborted) setSupervisionEnabled(true)
      })
    return () => controller.abort()
  }, [botId, isOpenSupervisionManagementModal, isQcs])

  useEffect(() => {
    if (!tabs.includes(selectedTab)) {
      setSelectedTab(tabs[0])
    }
  }, [tabs, selectedTab])

  useEffect(() => {
    if (currentSession.status) {
      updateUserSupervisionStatus(selectedUser.id, currentSession.status)
    }
  }, [currentSession.status])

  const modalTitle = useMemo(() => {
    return `${selectedUser.first_name} ${selectedUser.last_name}'s supervisions`
  }, [selectedUser])

  const completeSupervisionHandler = () => {
    setCurrentSession(prevState => ({
      ...prevState,
      summary: {
        ...prevState.summary,
        managerSummary: '',
      },
    }))
  }

  const modalIcon = () => {
    return (
      <>
        <ReactTooltip
          className="modal-tooltip"
          place="top"
          type="dark"
          effect="solid"
          id="DatePickerTooltip-1"
          show={true}>
          A scheduled supervision to review the care professional’s performance, recurring at the interval defined by
          the organization or team settings.
        </ReactTooltip>
        <img
          style={{ cursor: 'pointer', marginLeft: '10px' }}
          data-tip
          data-for="DatePickerTooltip-1"
          src="/images/platform/information-button.svg"
          alt="info icon"
        />
      </>
    )
  }

  return (
    <ModalTemplate
      title={modalTitle}
      titleIcon={modalIcon()}
      open={isOpenSupervisionManagementModal}
      onSave={completeSupervisionHandler}
      onClose={onClose}
      hasPrimaryButton={false}
      primaryButtonTitle="Complete supervision"
      containerClasses="managerSupervisionModal"
      isPrimaryButtonDisabled={!currentSession.summary?.managerSummary}>
      <>
        <div className={classes.tabs}>
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={selectedTab === tab ? classes.activeTab : classes.tab}
              onClick={() => setSelectedTab(tab)}>
              {tab}
            </div>
          ))}
        </div>
        <div
          className={classNames(classes.body, classes.supervisionBody)}
          id="scrollable"
          // 228px = container padding + header + tabs + footer, so the popup tops out at ModalTemplate's 95vh.
          style={{ height: 'calc(95vh - 228px)', maxHeight: 386 }}>
          {selectedTab === 'Current' && supervisionEnabled && (
            <CurrentSupervisionTab
              botId={botId}
              selectedUser={selectedUser}
              currentSession={currentSession}
              setCurrentSession={setCurrentSession}
              hasExtraUpdate={hasExtraUpdate}
              setHasExtraUpdate={setHasExtraUpdate}
              isPrimaryButtonDisabled={!currentSession.summary?.managerSummary}
            />
          )}
          {selectedTab === 'Completed' && <CompletedSupervisionTab botId={botId} selectedUser={selectedUser} />}
        </div>
      </>
    </ModalTemplate>
  )
}
